import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const filterFieldDefaults = {
  source: "",
  sourceType: "ALL",
  metaPageId: "",
  metaAdAccountId: "",
  campaignId: "",
  role: "",
  user: "",
  assignmentStatus: "all",
  branchId: "",
  noBranch: false,
  priority: "",
  gender: "",
  phoneCountryCode: "",
  stage: "",
  status: "",
  subStatus: "",
  inactiveFor: 0,
  /**
   * Aligned idle filter (days) — mirrors the Paused Activity Leads REPORT's definition
   * exactly (OPEN/non-rejected leads idle by the last-touch proxy ≥ N days), so
   * the report's branch/user drill lands here with an IDENTICAL count. Distinct
   * from `inactiveFor` (updatedAt-only, any status). 0 = off.
   */
  idleOpenFor: 0,
  /**
   * Which activity kinds the paused-activity filter counts — comma list of
   * server tokens (updates|followups|notes|calls). "" = ALL kinds, which is
   * the default definition and what the report widget uses, so it is NOT an
   * applied filter on its own. Only meaningful when `idleOpenFor` > 0;
   * serialized to `?idleTouches=` (real subsets only — see
   * useLeadFilterParams).
   */
  idleTouches: "",
  lastContactedStartDate: "",
  lastContactedEndDate: "",
  contactedBy: "",
  nextFollowupDate: "",
  followupStartDate: "",
  followupEndDate: "",
  // TRACK follow-ups (their own table) — the FollowUp section's "Tracks" side.
  trackFollowupStartDate: "",
  trackFollowupEndDate: "",
  trackFollowupTrackId: "",
  lastActivityDate: "",
  /**
   * Call filters — see `FilterState` in FilterContext for the semantics.
   * `callActivity: "none"` inverts the whole section into "no such call".
   */
  callActivity: "",
  callDirection: "",
  callOutcome: "",
  callCategory: "",
  callBy: "",
  targetLineId: "",
  targetUserId: "",
  targetAsOf: "",
  callStartDate: "",
  callEndDate: "",
  callStartTime: "",
  callEndTime: "",
  minCallDuration: 0,
  maxCallDuration: 0,
  minCallCount: 0,
  maxCallCount: 0,
  hasCallRecording: false,
  hasChoices: false,
  numberOfChoices: 0,
  choicesAppliedIn: "",
  choicesLastUpdated: "",
  startDate: "",
  endDate: "",
  /**
   * ACTOR-ACTIVITY filter — "leads that had this kind of activity in the
   * period (by these users)". Pushed by the reports activity explorer's ↗;
   * serialized to ?activityType/activityBy/activityStart/activityEnd. Types:
   * followup_created | followup_completed | call | note | chat | whatsapp |
   * email | stage_move (see server lead.activity-filter.ts).
   */
  activityType: "",
  activityBy: "",
  activityStart: "",
  activityEnd: "",
  /**
   * Optional IST wall-clock narrowing of the date range, 24-hour "HH:mm"
   * ("" = whole day). Endpoints of ONE continuous window — see FilterState.
   * Serialized to `?startTime=` / `?endTime=`.
   */
  startTime: "",
  endTime: "",
  dateType: "createdAt",
  /**
   * Status-Changed-At target stages (see FilterState.stageChangedTo) — comma
   * list, matched as a union. `""` = Any stage change. Serialized to
   * `?stageChangedToStageId=<id>[,<id>…]`.
   */
  stageChangedTo: "",
  /**
   * ENTRY COUNTS LEADS THAT MOVED ON. Under "Status Changed At" with a stage
   * target, the count is normally "entered the stage in the range AND is
   * still there" — so today's intake shrinks as the team works it. With this
   * on, a lead that entered the stage inside the range counts wherever it
   * sits now (owner, 2026-09-25: "stage changed to Eligible current day, even
   * if it goes to another stage, it should be in the count"). Serialized to
   * `?entryMovedOn=true`; a superset of the strict reading, never smaller.
   */
  entryMovedOn: false,
  /**
   * dateType="statusChangedAt" companion (see FilterState.intakeStageId) —
   * the intake stage id so the server windows that stage's bucket on
   * `createdAt`. Serialized to `?intakeStageId=`.
   */
  intakeStageId: "",
  /**
   * Which level's change "Status Changed At" counts (see
   * FilterState.entryLevel): "" = stage moves, "status" / "subStatus" = the
   * status / sub-status clock. Serialized to `?entryLevel=`.
   */
  entryLevel: "",
  /**
   * "lead" = stage/status/sub-status match the lead's OWN row only (see
   * FilterState.stageGrain — report drill ↗s). Serialized to `?stageGrain=`,
   * either "lead" (bucket by the lead's own row) or "matchedDeal" (bucket by
   * the representative deal carrying the active deal-grain custom field; the
   * server falls back to "lead" when there is no such filter).
   */
  stageGrain: "",
  /**
   * "lead" = the Campaign filter matches the lead's OWN campaign only (see
   * FilterState.campaignGrain — report drill ↗s). Serialized to
   * `?campaignGrain=lead`.
   */
  campaignGrain: "",
  /**
   * Meta ad set / ad (the creative) — the drawer's Source & Campaign "Ad
   * set" / "Ad" pickers or a report ↗; comma lists of row ids
   * matched against the lead's OWN first-touch ad set / ad (see
   * FilterState.adsetId). Serialized to `?adsetId=` / `?adId=`.
   */
  adsetId: "",
  adId: "",
  /**
   * The REPORT BAR's Intake pill, carried by a report drill ↗ (see
   * FilterState.intakeKeys): `"<year>-<month0>"` keys, comma-joined, exactly
   * as the bar sends them to every report endpoint. Serialized to
   * `?intakeKeys=`.
   */
  intakeKeys: "",
  /**
   * The dashboard's follow-up board strip ↗ ("N follow-ups on unassigned
   * leads / on leads with no branch"): the card's exact request as JSON. The
   * server pins the list to the leads that strip counted, so the list IS the
   * number (`followUpLoadDrill.ts`). Serialized to `?followUpLoad=`.
   */
  followUpLoad: "",
  /**
   * "ongoing" | "rejected" — the LEAD-STATUS rejection rule (the lead's own
   * Status OR SubStatus carries the rejection mark), which is what the reports
   * surface counts its Active / Inactive pill with (the WIRE values stay
   * ongoing/rejected). Deliberately distinct from `isActive`, the DEAL-liveness
   * rule: a lead in an ordinary status whose only deal was rejected is inactive
   * under `isActive` but still active here. Set by report drill
   * ↗s so the opened list uses the same rule as the number clicked.
   * "" = no outcome narrowing. Serialized to `?leadOutcome=`.
   */
  leadOutcome: "",
  /**
   * Archived-lead policy. "" = page default (archived hidden); "all" = include
   * archived — set by report drill ↗s whose widget counted archived leads
   * (reports count archived everywhere, 2026-08-11), so the opened list is the
   * SAME population as the number clicked. Serialized to `?archived=`.
   */
  archived: "",
  isRevoked: false,
  isReenquired: false as boolean | "all", // "all" = every re-enquiry (toolbar button); true = badge-carrying only
  isActive: "all",
  /**
   * Active ANCHOR filter (old-CRM "uniquify" port). Holds the SELECTED anchor's
   * stage id (one anchor active at a time), or `null` when off. The filter LOGIC
   * + button name are admin-configured on that `Stage.isAnchorPoint` stage.
   * Serialized to `?verified=<stageId>`.
   */
  verifiedAnchor: null as number | null,
  /**
   * Sub-bucket within the Revoked tab — replaces the [All, Active, Inactive]
   * secondary toggle when `isRevoked` is true. Values match the server's
   * `RevokeType` enum. Ignored when `isRevoked` is false.
   */
  revokeType: "all" as "all" | "noAccess" | "branchChange" | "reassigned",
  trackId: "",
  subTrackId: "",
  trackStatus: "",
  // Track STARTED window (first status attached) — set by the dashboard track widgets' ↗.
  trackStartDate: "",
  trackEndDate: "",
  hasActiveTrack: false,
  paymentStatus: "",
  hasDealValue: false,
  hasPendingAmount: false,
  hasInstallmentPlan: false,
  hasOverdueInstallments: false,
  instRules: "",
  instDue: "",
  minDealValue: 0,
  maxDealValue: 0,
  minPendingAmount: 0,
  maxPendingAmount: 0,
  nicheFilters: {} as Record<string, string | number | boolean>,
  customFieldFilters: {} as Record<string, string>,
  /**
   * Correlated custom-field selections (Intake Year × Intake Month) — see
   * `FilterState.customFieldPairs`. Serialized to `?cfPair_<a>_<b>=…`.
   */
  customFieldPairs: {} as Record<string, string>,
};

export type FilterFieldDefaults = typeof filterFieldDefaults;

const initialState = {
  search: "",
  ...filterFieldDefaults,
  appliedFilters: {} as Record<string, any>,
  /**
   * Has the tenant's "Default view" (Settings → Manage) already had its ONE
   * chance to seed `isActive` for this filter state?
   *
   * Deliberately OUTSIDE `filterFieldDefaults` — it is bookkeeping, not a
   * filter, so `applyFilters` never reports it as applied and it never
   * reaches the query string.
   *
   * `clearFilters` sets it TRUE, which is the whole point: every report /
   * dashboard / campaign ↗ clears the filters and then writes its own before
   * navigating to the list, and the opened list must reproduce the number that
   * was clicked. Seeding "Active" underneath such a hand-off would quietly
   * narrow it — the cell would say 166 and its list would open 140. So once
   * anything has cleared this state, the tenant default stays out of it until
   * the next full load.
   */
  defaultViewSeeded: false,
};

const leadFilterSlice = createSlice({
  name: "leadFilter",
  initialState,
  reducers: {
    setLeadSearch(state, action) {
      state.search = action.payload;
    },
    setLeadSource(state, action) {
      state.source = action.payload;
    },
    setLeadRole(state, action) {
      state.role = action.payload;
    },
    setLeadUser(state, action) {
      state.user = action.payload;
    },
    setLeadAssignmentStatus(state, action) {
      state.assignmentStatus = action.payload;
    },
    setLeadStage(state, action) {
      state.stage = action.payload;
    },
    setLeadStatus(state, action) {
      state.status = action.payload;
    },
    setLeadSubStatus(state, action) {
      state.subStatus = action.payload;
    },
    setLeadInactiveFor(state, action) {
      state.inactiveFor = action.payload;
    },
    setLeadLastContactedStartDate(state, action) {
      state.lastContactedStartDate = action.payload;
    },
    setLeadLastContactedEndDate(state, action) {
      state.lastContactedEndDate = action.payload;
    },
    setLeadContactedBy(state, action) {
      state.contactedBy = action.payload;
    },
    setLeadNextFollowupDate(state, action) {
      state.nextFollowupDate = action.payload;
    },
    setLeadLastActivityDate(state, action) {
      state.lastActivityDate = action.payload;
    },
    setLeadHasChoices(state, action) {
      state.hasChoices = action.payload;
    },
    setLeadNumberOfChoices(state, action) {
      state.numberOfChoices = action.payload;
    },
    setLeadChoicesAppliedIn(state, action) {
      state.choicesAppliedIn = action.payload;
    },
    setLeadChoicesLastUpdated(state, action) {
      state.choicesLastUpdated = action.payload;
    },
    setLeadStartDate(state, action) {
      state.startDate = action.payload;
    },
    setLeadEndDate(state, action) {
      state.endDate = action.payload;
    },
    setLeadDateType(state, action) {
      state.dateType = action.payload;
    },
    setLeadFilterField(
      state,
      action: PayloadAction<{ key: keyof typeof initialState; value: any }>
    ) {
      const { key, value } = action.payload;
      (state as any)[key] = value;
    },
    /** Merges a single key/value into nicheFilters without destroying siblings */
    setNicheFilterField(
      state,
      action: PayloadAction<{ key: string; value: string | number | boolean }>
    ) {
      const { key, value } = action.payload;
      state.nicheFilters = { ...state.nicheFilters, [key]: value };
    },
    applyFilters(state) {
      // Call window: one picked date IS a complete filter — mirror it to the
      // other bound at apply, so the state, the chips and the query all read
      // "that single day" (the serializer mirrors too, as a belt-and-braces).
      if (state.callStartDate && !state.callEndDate)
        state.callEndDate = state.callStartDate;
      else if (state.callEndDate && !state.callStartDate)
        state.callStartDate = state.callEndDate;
      // A call-time window only means anything on top of a call date window.
      if (!state.callStartDate && !state.callEndDate) {
        state.callStartTime = "";
        state.callEndTime = "";
      }
      const applied: Record<string, any> = {};
      const current = state as Record<string, any>;
      for (const [key, defaultVal] of Object.entries(filterFieldDefaults)) {
        if (
          key === "nicheFilters" ||
          key === "customFieldFilters" ||
          key === "customFieldPairs"
        ) {
          const obj = current[key] as Record<string, unknown>;
          if (obj && Object.keys(obj).length > 0) {
            applied[key] = obj;
          }
        } else if (current[key] !== defaultVal) {
          applied[key] = current[key];
        }
      }
      state.appliedFilters = applied;
    },
    clearFilters() {
      // ...and the tenant "Default view" seed is spent — see the flag's note.
      return { ...initialState, defaultViewSeeded: true };
    },
    /** The "Default view" seed has been considered (applied or deliberately
     *  skipped) for this filter state; never look at it again. */
    markDefaultViewSeeded(state) {
      state.defaultViewSeeded = true;
    },
  },
});

export const {
  setLeadSearch,
  setLeadSource,
  setLeadRole,
  setLeadUser,
  setLeadStage,
  setLeadStatus,
  setLeadSubStatus,
  setLeadAssignmentStatus,
  setLeadInactiveFor,
  setLeadLastContactedStartDate,
  setLeadLastContactedEndDate,
  setLeadContactedBy,
  setLeadNextFollowupDate,
  setLeadLastActivityDate,
  setLeadHasChoices,
  setLeadNumberOfChoices,
  setLeadChoicesAppliedIn,
  setLeadChoicesLastUpdated,
  setLeadStartDate,
  setLeadEndDate,
  setLeadDateType,
  setLeadFilterField,
  setNicheFilterField,
  applyFilters,
  clearFilters,
  markDefaultViewSeeded,
} = leadFilterSlice.actions;
export default leadFilterSlice.reducer;

export const selectedLeadFilters = (state: any) => state.leadFilter;
